// Script parser + static validator (SCR-1). PURE (ENG-1).
//
// Recursive-descent over the lexer's token stream → a small typed AST, followed by
// a resolve/validate pass that (a) rejects any call to a name outside the API
// whitelist — which is how `fetch`/`window`/`import` are kept out of the grammar —
// and (b) rejects a read of an undeclared variable, both with a source position.
// `compile()` throws a positioned ScriptError; `tryCompile()` wraps it in a result.
//
// Functions (SCR-5, ARCH §27.2) are declared `fn name(p1..p4) { … return expr }`, at
// TOP LEVEL only — one inside an `if`/`repeat`/`on` block is a compile error. Each
// declaration records its callee edges, and `compile()` runs a DFS that rejects any
// cycle (direct or mutual) naming it, so no recursion survives compilation: that is
// what makes the static cost bound (§27.3) and the capability set (§27.11) finite.
// Names resolve **user fn first, then the verb table** — safe because a `fn` named
// after a verb is refused at its declaration, so a user fn can never shadow `say` or
// slip past its VERB_SIGNATURES arity check.
//
// Since M45.4 the verb table is SLICED BY HOST KIND (ARCH §27.5): `compile()` takes the
// `ScriptHostKind` the source is being compiled for and refuses a verb outside that
// slice — `fill`/`give` in a mob script, `self.*`/`mem.*`/`handled` in a block one —
// as a compile error naming the verb AND the host kind (§27.5: "never a runtime
// surprise", E-47 kin). The slice table itself lives with the verbs in `api.ts`; this
// file only asks `verbAllowedIn`, at the same point it checks arity.
import { tokenize, ScriptError, type Token, type Pos } from './lex';
import { VERB_SIGNATURES, NAMESPACE_PROPS, verbAllowedIn, type ScriptHostKind } from '../api';
import { programCost, overBudget, overBudgetError } from './cost';
import { checkDeclared } from '../capabilities';

/** Positional parameters a `fn` may take (ARCH §27.2 `fn name(p1..p4)`). Part of the
 *  grammar, not a tunable, so it lives with the grammar rather than in `sim/config`. */
const FN_MAX_PARAMS = 4;

/** The slice a caller that names no host kind gets (ARCH §27.5).
 *
 *  `'block'` because that is what every pre-M45 caller was compiling, so an omitted
 *  argument keeps the block slice exactly as it was. It is also the FAIL-CLOSED choice
 *  for the new one: a mob source compiled without its kind then fails loudly on its
 *  first `self.*` call instead of quietly being granted a block's `fill`/`give`. */
const DEFAULT_HOST_KIND: ScriptHostKind = 'block';

export type Expr =
  | { kind: 'num'; value: number; pos: Pos }
  | { kind: 'str'; value: string; pos: Pos }
  | { kind: 'bool'; value: boolean; pos: Pos }
  | { kind: 'var'; name: string; pos: Pos }
  | { kind: 'unary'; op: '-' | '!'; operand: Expr; pos: Pos }
  | { kind: 'binary'; op: string; left: Expr; right: Expr; pos: Pos }
  | { kind: 'member'; obj: Expr; prop: string; pos: Pos }
  | { kind: 'call'; callee: Expr; args: Expr[]; pos: Pos };

export type Stmt =
  | { kind: 'let'; name: string; expr: Expr; pos: Pos }
  | { kind: 'assign'; name: string; expr: Expr; pos: Pos }
  | { kind: 'if'; cond: Expr; then: Stmt[]; otherwise?: Stmt[]; pos: Pos }
  | { kind: 'repeat'; count: Expr; body: Stmt[]; pos: Pos }
  | { kind: 'on'; event: string; body: Stmt[]; pos: Pos }
  | { kind: 'return'; expr: Expr; pos: Pos }
  | { kind: 'expr'; expr: Expr; pos: Pos };

/** A top-level `fn`. `callees` are the names its body calls, in first-appearance
 *  order and de-duplicated — user fns AND verbs, recorded at parse time — which is
 *  the call graph the acyclicity DFS walks (and the edge set §27.3/§27.11 fold over). */
export interface FnDecl {
  kind: 'fn';
  name: string;
  params: string[];
  body: Stmt[];
  callees: string[];
  pos: Pos;
}

export interface Program {
  body: Stmt[];
  fns: FnDecl[];
  /** The `capabilities { … }` block's contents, or **null** when the source has no
   *  block at all — which §27.11 treats as "its computed set IS its declaration",
   *  so every pre-M44 source keeps compiling unchanged. An empty block is NOT null:
   *  `capabilities { }` declares nothing and refuses every host verb. */
  declaredCapabilities: ReadonlySet<string> | null;
}

export type CompileResult = { ok: true; program: Program } | { ok: false; error: ScriptError };

class Parser {
  private i = 0;
  constructor(private readonly toks: Token[]) {}

  private peek(n = 0): Token { return this.toks[Math.min(this.i + n, this.toks.length - 1)]!; }
  private next(): Token { return this.toks[this.i++]!; }
  private isOp(text: string, n = 0): boolean { const t = this.peek(n); return t.kind === 'op' && t.text === text; }
  private isKw(text: string, n = 0): boolean { const t = this.peek(n); return t.kind === 'keyword' && t.text === text; }
  private matchOp(text: string): boolean { if (this.isOp(text)) { this.i++; return true; } return false; }
  private expectOp(text: string): void { if (!this.matchOp(text)) throw new ScriptError(`expected '${text}'`, this.peek().pos); }
  private expectIdent(what: string): string {
    const t = this.peek();
    if (t.kind !== 'ident') throw new ScriptError(`expected ${what}`, t.pos);
    this.i++;
    return t.text;
  }

  parseProgram(): Program {
    const body: Stmt[] = [];
    const fns: FnDecl[] = [];
    let declaredCapabilities: ReadonlySet<string> | null = null;
    // `fn` is handled HERE and nowhere else, so `stmt()` — the only path into a
    // block body — rejects a nested declaration (ARCH §27.2 "top-level only").
    while (this.peek().kind !== 'eof') {
      if (this.isKw('capabilities')) {
        // §27.11: at most one, and before any handler — a manifest that appears after
        // the code it is meant to describe reads as an afterthought, so it is refused.
        if (declaredCapabilities) throw new ScriptError('only one capabilities block is allowed', this.peek().pos);
        if (body.some((s) => s.kind === 'on')) throw new ScriptError('the capabilities block must come before any `on` handler', this.peek().pos);
        declaredCapabilities = this.capabilitiesBlock();
      } else if (this.isKw('fn')) fns.push(this.fnDecl());
      else body.push(this.stmt());
    }
    return { body, fns, declaredCapabilities };
  }

  /** `capabilities { a.b, c.d }` — a set of dotted names, trailing comma allowed, an
   *  empty block legal. Only `parseProgram` calls this, so the block cannot nest. */
  private capabilitiesBlock(): ReadonlySet<string> {
    this.next(); // 'capabilities'
    this.expectOp('{');
    const out = new Set<string>();
    while (!this.isOp('}')) {
      const pos = this.peek().pos;
      let name = this.expectIdent('a capability name');
      while (this.isOp('.')) { this.next(); name += `.${this.expectIdent('a capability name')}`; }
      if (out.has(name)) throw new ScriptError(`capability '${name}' is listed twice`, pos);
      out.add(name);
      if (this.isOp(',')) this.next();
      else break;
    }
    this.expectOp('}');
    return out;
  }

  /** `fn name(p1, …) { … }`. Only `parseProgram` calls this. */
  private fnDecl(): FnDecl {
    const pos = this.next().pos; // 'fn'
    const name = this.expectIdent('a function name');
    const params = this.params(name);
    const body = this.block();
    return { kind: 'fn', name, params, body, callees: collectCallees(body), pos };
  }

  private params(fnName: string): string[] {
    this.expectOp('(');
    const out: string[] = [];
    if (!this.isOp(')')) {
      this.param(out, fnName);
      while (this.matchOp(',')) this.param(out, fnName);
    }
    this.expectOp(')');
    return out;
  }

  private param(out: string[], fnName: string): void {
    const t = this.peek();
    if (out.length >= FN_MAX_PARAMS) throw new ScriptError(`fn '${fnName}' takes at most ${FN_MAX_PARAMS} parameters`, t.pos);
    const name = this.expectIdent('a parameter name');
    if (out.includes(name)) throw new ScriptError(`duplicate parameter '${name}' in fn '${fnName}'`, t.pos);
    out.push(name);
  }

  private block(): Stmt[] {
    this.expectOp('{');
    const out: Stmt[] = [];
    while (!this.isOp('}')) {
      if (this.peek().kind === 'eof') throw new ScriptError("expected '}'", this.peek().pos);
      out.push(this.stmt());
    }
    this.expectOp('}');
    return out;
  }

  private stmt(): Stmt {
    const t = this.peek();
    if (this.isKw('let')) {
      const pos = this.next().pos;
      const name = this.expectIdent('a variable name');
      this.expectOp('=');
      return { kind: 'let', name, expr: this.expr(), pos };
    }
    if (this.isKw('if')) return this.ifStmt();
    if (this.isKw('repeat')) {
      const pos = this.next().pos;
      const count = this.expr();
      return { kind: 'repeat', count, body: this.block(), pos };
    }
    if (this.isKw('on')) {
      const pos = this.next().pos;
      const event = this.expectIdent('an event name');
      return { kind: 'on', event, body: this.block(), pos };
    }
    if (this.isKw('return')) {
      const pos = this.next().pos;
      return { kind: 'return', expr: this.expr(), pos };
    }
    // A `fn` only ever reaches `stmt()` from inside a block — the top level parses
    // declarations itself (ARCH §27.2).
    if (this.isKw('fn')) throw new ScriptError("'fn' may only be declared at the top level", t.pos);
    // Assignment: `ident = …` (but not `==`).
    if (t.kind === 'ident' && this.isOp('=', 1)) {
      const name = this.next().text;
      this.next(); // '='
      return { kind: 'assign', name, expr: this.expr(), pos: t.pos };
    }
    const e = this.expr();
    return { kind: 'expr', expr: e, pos: e.pos };
  }

  private ifStmt(): Stmt {
    const pos = this.next().pos; // 'if'
    const cond = this.expr();
    const then = this.block();
    let otherwise: Stmt[] | undefined;
    if (this.isKw('else')) {
      this.next();
      otherwise = this.isKw('if') ? [this.ifStmt()] : this.block();
    }
    return { kind: 'if', cond, then, otherwise, pos };
  }

  // ── Expression precedence (low → high) ──────────────────────────────────────
  private expr(): Expr { return this.or(); }
  private or(): Expr { return this.leftAssoc(() => this.and(), ['||']); }
  private and(): Expr { return this.leftAssoc(() => this.equality(), ['&&']); }
  private equality(): Expr { return this.leftAssoc(() => this.comparison(), ['==', '!=']); }
  private comparison(): Expr { return this.leftAssoc(() => this.term(), ['<', '<=', '>', '>=']); }
  private term(): Expr { return this.leftAssoc(() => this.factor(), ['+', '-']); }
  private factor(): Expr { return this.leftAssoc(() => this.unary(), ['*', '/', '%']); }

  private leftAssoc(sub: () => Expr, ops: string[]): Expr {
    let left = sub();
    while (this.peek().kind === 'op' && ops.includes(this.peek().text)) {
      const op = this.next();
      const right = sub();
      left = { kind: 'binary', op: op.text, left, right, pos: op.pos };
    }
    return left;
  }

  private unary(): Expr {
    if (this.isOp('-') || this.isOp('!')) {
      const op = this.next();
      return { kind: 'unary', op: op.text as '-' | '!', operand: this.unary(), pos: op.pos };
    }
    return this.postfix();
  }

  private postfix(): Expr {
    let e = this.primary();
    while (this.matchOp('.')) {
      const prop = this.expectIdent('a property name');
      if (this.isOp('(')) e = { kind: 'call', callee: { kind: 'member', obj: e, prop, pos: e.pos }, args: this.args(), pos: e.pos };
      else e = { kind: 'member', obj: e, prop, pos: e.pos };
    }
    return e;
  }

  private primary(): Expr {
    const t = this.peek();
    if (t.kind === 'num') { this.i++; return { kind: 'num', value: t.num!, pos: t.pos }; }
    if (t.kind === 'str') { this.i++; return { kind: 'str', value: t.str!, pos: t.pos }; }
    if (this.isKw('true') || this.isKw('false')) { this.i++; return { kind: 'bool', value: t.text === 'true', pos: t.pos }; }
    if (t.kind === 'ident') {
      this.i++;
      if (this.isOp('(')) return { kind: 'call', callee: { kind: 'var', name: t.text, pos: t.pos }, args: this.args(), pos: t.pos };
      return { kind: 'var', name: t.text, pos: t.pos };
    }
    if (this.matchOp('(')) { const e = this.expr(); this.expectOp(')'); return e; }
    throw new ScriptError('expected an expression', t.pos);
  }

  private args(): Expr[] {
    this.expectOp('(');
    const out: Expr[] = [];
    if (!this.isOp(')')) {
      out.push(this.expr());
      while (this.matchOp(',')) out.push(this.expr());
    }
    this.expectOp(')');
    return out;
  }
}

// ── The call graph (SCR-5 / ARCH §27.2) ────────────────────────────────────────

/** The dotted name a call names, or null when the callee is not a plain name (which
 *  validation reports as "not callable"). */
function calleeName(e: Expr & { kind: 'call' }): string | null {
  const c = e.callee;
  if (c.kind === 'var') return c.name;
  if (c.kind === 'member' && c.obj.kind === 'var') return `${c.obj.name}.${c.prop}`;
  return null;
}

/** Every name a fn body calls, first-appearance order, de-duplicated. Purely
 *  syntactic (it runs before validation), so unresolvable names are recorded too and
 *  the DFS/cost/capability walks simply ignore the ones they do not own. */
function collectCallees(body: Stmt[]): string[] {
  const out: string[] = [];
  const walkStmts = (stmts: Stmt[]): void => { for (const s of stmts) walkStmt(s); };
  const walkStmt = (s: Stmt): void => {
    switch (s.kind) {
      case 'let': case 'assign': case 'expr': case 'return': walkExpr(s.expr); return;
      case 'if': walkExpr(s.cond); walkStmts(s.then); if (s.otherwise) walkStmts(s.otherwise); return;
      case 'repeat': walkExpr(s.count); walkStmts(s.body); return;
      case 'on': walkStmts(s.body); return;
    }
  };
  const walkExpr = (e: Expr): void => {
    switch (e.kind) {
      case 'num': case 'str': case 'bool': case 'var': return;
      case 'unary': walkExpr(e.operand); return;
      case 'binary': walkExpr(e.left); walkExpr(e.right); return;
      case 'member': walkExpr(e.obj); return;
      case 'call': {
        const name = calleeName(e);
        if (name !== null && !out.includes(name)) out.push(name);
        for (const a of e.args) walkExpr(a);
        return;
      }
    }
  };
  walkStmts(body);
  return out;
}

/** Index the top-level `fn`s by name. A duplicate is refused, and so is a name that
 *  collides with the API surface: calls resolve user fn FIRST, so allowing `fn say()`
 *  would silently break the real `say` verb and its VERB_SIGNATURES check (§27.2). */
function indexFns(fns: FnDecl[]): Map<string, FnDecl> {
  const out = new Map<string, FnDecl>();
  for (const fn of fns) {
    if (out.has(fn.name)) throw new ScriptError(`fn '${fn.name}' is already defined`, fn.pos);
    if (VERB_SIGNATURES[fn.name]) throw new ScriptError(`'${fn.name}' is a built-in verb and cannot be used as a fn name`, fn.pos);
    if (NAMESPACE_PROPS[fn.name]) throw new ScriptError(`'${fn.name}' is a built-in name and cannot be used as a fn name`, fn.pos);
    out.set(fn.name, fn);
  }
  return out;
}

/** Reject any cycle in the call graph — the language's no-recursion guarantee, made
 *  **by construction** rather than by a runtime stack guard (ARCH §27.2). A classic
 *  three-colour DFS: re-entering a grey fn closes a cycle, and the path stack names
 *  it (`a -> b -> a`) so the author can see exactly which edge to cut. */
function assertAcyclic(fns: ReadonlyMap<string, FnDecl>): void {
  const seen = new Map<string, 'open' | 'done'>();
  const path: string[] = [];
  const visit = (name: string): void => {
    const fn = fns.get(name);
    if (!fn) return; // a verb, or an unknown name validation will report
    const state = seen.get(name);
    if (state === 'done') return;
    if (state === 'open') {
      const cycle = [...path.slice(path.indexOf(name)), name];
      throw new ScriptError(`recursive fn call cycle: ${cycle.join(' -> ')}`, fn.pos);
    }
    seen.set(name, 'open');
    path.push(name);
    for (const callee of fn.callees) visit(callee);
    path.pop();
    seen.set(name, 'done');
  };
  for (const name of fns.keys()) visit(name);
}

// ── Static validation: undeclared names + non-whitelisted calls (SCR-2/E-30) ───
type Scopes = Set<string>[];

/** What the walk needs beyond the scope chain: the fn table calls resolve against,
 *  whether we are inside a fn body (the only place `return` is legal), and the host
 *  slice every call is checked against (§27.5). The slice is carried on the ctx, not
 *  passed alongside it, so a `fn` body is checked against the SAME slice as the
 *  handler that calls it — hiding `fill` behind a function must not launder it. */
interface Ctx {
  readonly fns: ReadonlyMap<string, FnDecl>;
  readonly inFn: boolean;
  readonly host: ScriptHostKind;
}

function declared(scopes: Scopes, name: string): boolean {
  for (const s of scopes) if (s.has(name)) return true;
  return false;
}
function ensureDeclared(scopes: Scopes, name: string, pos: Pos): void {
  if (!declared(scopes, name)) throw new ScriptError(`unknown name '${name}'`, pos);
}

function validateStmts(stmts: Stmt[], scopes: Scopes, ctx: Ctx): void {
  for (const s of stmts) validateStmt(s, scopes, ctx);
}

function validateStmt(s: Stmt, scopes: Scopes, ctx: Ctx): void {
  switch (s.kind) {
    case 'let':
      validateExpr(s.expr, scopes, ctx); // initializer is evaluated before the name exists
      scopes[scopes.length - 1]!.add(s.name);
      break;
    case 'assign':
      ensureDeclared(scopes, s.name, s.pos);
      validateExpr(s.expr, scopes, ctx);
      break;
    case 'if':
      validateExpr(s.cond, scopes, ctx);
      validateStmts(s.then, [...scopes, new Set()], ctx);
      if (s.otherwise) validateStmts(s.otherwise, [...scopes, new Set()], ctx);
      break;
    case 'repeat':
      validateExpr(s.count, scopes, ctx);
      validateStmts(s.body, [...scopes, new Set()], ctx);
      break;
    case 'on':
      validateStmts(s.body, [...scopes, new Set()], ctx);
      break;
    case 'return':
      if (!ctx.inFn) throw new ScriptError("'return' may only be used inside a fn", s.pos);
      validateExpr(s.expr, scopes, ctx);
      break;
    case 'expr':
      validateExpr(s.expr, scopes, ctx);
      break;
  }
}

function validateExpr(e: Expr, scopes: Scopes, ctx: Ctx): void {
  switch (e.kind) {
    case 'num': case 'str': case 'bool': return;
    case 'var': ensureDeclared(scopes, e.name, e.pos); return;
    case 'unary': validateExpr(e.operand, scopes, ctx); return;
    case 'binary': validateExpr(e.left, scopes, ctx); validateExpr(e.right, scopes, ctx); return;
    case 'member': validateMember(e, scopes, ctx); return;
    case 'call': validateCall(e, scopes, ctx); return;
  }
}

function validateMember(e: Expr & { kind: 'member' }, scopes: Scopes, ctx: Ctx): void {
  const base = e.obj;
  if (base.kind === 'var') {
    if (declared(scopes, base.name)) return; // a vec3 var; the runtime checks the prop
    const props = NAMESPACE_PROPS[base.name];
    if (props) {
      if (!props.has(e.prop)) throw new ScriptError(`'${base.name}' has no property '${e.prop}'`, e.pos);
      return;
    }
    throw new ScriptError(`unknown name '${base.name}'`, e.pos);
  }
  validateExpr(base, scopes, ctx); // e.g. vec3(1,2,3).x
}

function validateCall(e: Expr & { kind: 'call' }, scopes: Scopes, ctx: Ctx): void {
  const name = calleeName(e);
  if (name === null) throw new ScriptError('expression is not callable', e.pos);

  // A user `fn` resolves first, then the verb table. `indexFns` has already refused
  // a fn named after a verb, so this order can never shadow one (§27.2); an unknown
  // name falls through to the unchanged "unknown function" error.
  const fn = ctx.fns.get(name);
  const sig = fn ? { min: fn.params.length, max: fn.params.length } : VERB_SIGNATURES[name];
  if (!sig) throw new ScriptError(`unknown function '${name}'`, e.pos);
  // §27.5's host slice, checked BEFORE arity so `fill(1)` in a mob script reports the
  // real problem (the verb is not in this slice) rather than an argument count the
  // author could "fix" and still be refused. A user fn is never sliced — only the
  // verb table is the sandbox.
  if (!fn && !verbAllowedIn(name, ctx.host)) {
    throw new ScriptError(`'${name}' is not available to a ${ctx.host} script (ARCH §27.5 host slice)`, e.pos);
  }
  if (e.args.length < sig.min || e.args.length > sig.max) {
    const want = sig.min === sig.max ? `${sig.min}` : `${sig.min}–${sig.max}`;
    throw new ScriptError(`'${name}' expects ${want} argument${sig.max === 1 ? '' : 's'}, got ${e.args.length}`, e.pos);
  }
  // §27.10 / D-TF-105: an `emit` payload is ONE value — "not a list, not a bag". The
  // statically visible case is a payload that IS a list-producing call, and catching it
  // here makes it a COMPILE error, which is what §27.10 asks for; `callVerb` keeps a
  // runtime backstop for the lists this cannot see (one arriving through a fn param).
  if (!fn && name === 'emit') {
    const payload = e.args[1];
    const producer = payload?.kind === 'call' ? calleeName(payload) : null;
    if (producer === 'list' || producer === 'push') {
      throw new ScriptError(
        `'emit' takes a single value as its payload, not a list (ARCH §27.10)`,
        payload!.pos,
      );
    }
  }
  for (const a of e.args) validateExpr(a, scopes, ctx);
}

/** Lex → parse → validate. Throws a positioned ScriptError on any failure.
 *
 *  A fn body is validated against its PARAMS ONLY — never the caller's locals or the
 *  top-level `let`s — so a fn is a self-contained unit whose cost (§27.3) and
 *  capabilities (§27.11) fold into every caller unchanged. `self.*`/`event.*` stay
 *  readable inside one (they are namespaces, not variables).
 *
 *  `host` selects §27.5's verb slice (default `DEFAULT_HOST_KIND`); a verb outside it
 *  is refused here, which is the whole of the mob sandbox's compile-time half. */
export function compile(source: string, host: ScriptHostKind = DEFAULT_HOST_KIND): Program {
  const program = new Parser(tokenize(source)).parseProgram();
  const fns = indexFns(program.fns);
  assertAcyclic(fns);
  for (const fn of program.fns) validateStmts(fn.body, [new Set(fn.params)], { fns, inFn: true, host });
  validateStmts(program.body, [new Set()], { fns, inFn: false, host });
  // §27.11: the manifest must cover what the code actually reaches — through the
  // acyclic call graph, so a verb hidden behind a `fn` still counts. A source with no
  // block declares nothing and is satisfied by construction (back-compat).
  const missing = checkDeclared(program.declaredCapabilities, program.body, fns);
  if (missing) {
    throw new ScriptError(
      `verb '${missing.verb}' requires capability '${missing.capability}', which the capabilities block does not declare`,
    );
  }
  return program;
}

/** Never-throwing wrapper — the editor/host use this so a bad script surfaces an
 *  error message instead of crashing (E-30/E-33 philosophy).
 *
 *  `budget` applies §27.3's save-time refusal: a program whose worst-case static bound
 *  exceeds it comes back `{ok:false}` naming the computed op count and the offending
 *  line, instead of compiling and failing later at runtime. The bound is sound since
 *  D-TF-171 (it dominates the VM), so clearing this gate guarantees the dispatch fits.
 *
 *  It stays a **parameter rather than a global default** because the budget belongs to
 *  the target host, not to the compiler: `ScriptSystem.install` passes the host's
 *  budget and is where every real save is refused (§27.4). A caller with no host in
 *  hand — the editor's live compile as you type — omits it and gets a plain compile,
 *  so a half-typed script reports syntax errors rather than a cost it has not finished
 *  writing. E-30 remains the backstop for host-side cost (`FILL_MAX`), which no static
 *  bound covers.
 *
 *  `host` is §27.5's verb slice, forwarded to `compile`. `ScriptSystem.install` already
 *  knows its host kind, so it passes both; a caller with neither gets the block slice
 *  and today's behaviour (see `DEFAULT_HOST_KIND`). */
export function tryCompile(source: string, budget?: number, host?: ScriptHostKind): CompileResult {
  try {
    const program = compile(source, host ?? DEFAULT_HOST_KIND);
    if (budget !== undefined) {
      const cost = programCost(program, budget);
      if (overBudget(cost, budget)) return { ok: false, error: overBudgetError(cost, budget) };
    }
    return { ok: true, program };
  } catch (e) {
    if (e instanceof ScriptError) return { ok: false, error: e };
    return { ok: false, error: new ScriptError(`compile error: ${(e as Error).message}`) };
  }
}
